import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/db/client";
import { normalizeBacklogItem, asBacklogRow } from "@/lib/backlogUtils";
import type { BacklogItem } from "@/types/backlog";

/**
 * Global hook that syncs backlog items in real-time across the entire app.
 * Runs in AppContent so the cache stays fresh regardless of which page the user is on.
 *
 * Events carry no row data — only {table, op, id} plus the change-metadata
 * booleans the trigger computes (see notify_backlog_change() in
 * docs/schema.sql). Anything that needs the row refetches it under the
 * user's own RLS-scoped read.
 */
export const useGlobalBacklogSync = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const pendingUpdates = new Set<string>();
    let updateTimeout: ReturnType<typeof setTimeout> | null = null;

    const processPendingUpdates = () => {
      if (pendingUpdates.size > 0) {
        queryClient.invalidateQueries({ queryKey: ["backlog-items"] });
        queryClient.invalidateQueries({ queryKey: ["backlog-item"] });
        queryClient.invalidateQueries({ queryKey: ["subsheet-stats"] });
        pendingUpdates.clear();
      }
      updateTimeout = null;
    };

    const channel = supabase
      .channel("global-backlog-sync")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "backlog_items",
        },
        (payload) => {
          if (payload.eventType === "INSERT") {
            // Refetch the row RLS-scoped; a null result means this user
            // cannot see it (or it vanished already) — nothing to insert.
            void (async () => {
              const { data } = await supabase.from("backlog_items").select("*").eq("id", payload.id).maybeSingle();
              if (!data) return;
              const newItem = normalizeBacklogItem(asBacklogRow(data));
              queryClient.setQueryData(["backlog-items"], (old: BacklogItem[] | undefined) => {
                if (!old) return old;
                if (old.some((item) => item.id === newItem.id)) return old;
                return [...old, newItem].sort((a, b) => a.row_number - b.row_number);
              });
              queryClient.invalidateQueries({ queryKey: ["subsheet-stats"] });
            })();
          } else if (payload.eventType === "DELETE") {
            queryClient.setQueryData(["backlog-items"], (old: BacklogItem[] | undefined) => {
              if (!old) return old;
              return old.filter((item) => item.id !== payload.id);
            });
            queryClient.invalidateQueries({ queryKey: ["deleted-backlog-items"] });
            queryClient.invalidateQueries({ queryKey: ["backlog-item"] });
            queryClient.invalidateQueries({ queryKey: ["subsheet-stats"] });
          } else if (payload.eventType === "UPDATE") {
            // If deleted_at changed (soft-delete or restore), invalidate immediately
            if (payload.deleted_at_changed) {
              queryClient.invalidateQueries({ queryKey: ["backlog-items"] });
              queryClient.invalidateQueries({ queryKey: ["deleted-backlog-items"] });
              queryClient.invalidateQueries({ queryKey: ["backlog-item"] });
              queryClient.invalidateQueries({ queryKey: ["subsheet-stats"] });
              return;
            }

            // Skip row_number-only changes (bulk reordering noise)
            if (payload.row_number_only) {
              return;
            }

            // Batch meaningful updates with 100ms debounce
            pendingUpdates.add(payload.id);

            if (updateTimeout) {
              clearTimeout(updateTimeout);
            }
            updateTimeout = setTimeout(processPendingUpdates, 100);
          }
        },
      )
      .subscribe();

    return () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
};
